import React, { useEffect, useState } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
//helpers
import { verificateNullValue } from 'helpers';
// constants
import { choicesInvoice } from '../../../constants';
// components
import Button from 'components/backoffice/buttonCustom';
import CustomInput from 'components/backoffice/customInput';
import { SelectInputCustom } from 'components/input/index.js';
import SearchOrder from 'components/backoffice/customInput/SearchOrder';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import SearchCountry from 'components/backoffice/customInput/SearchCountry';
import SearchProvince from 'components/backoffice/customInput/SearchProvince';
import ArticlesHeader from 'components/backoffice/headers/articles';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const InvoiceForm = ({
  onSubmitForm,
  defaultValues,
  loading,
  isEditing
}) => {
  const { t } = useTranslation(['backoffice']);
  const [editBillingAddress, setEditBillingAddress] = useState(false);
  const [hidden, setHidden] = useState(true);
  const choices = choicesInvoice(t, false);

  const {
    formState: { errors },
    setValue,
    reset,
    control,
    handleSubmit
  } = useForm();

  useEffect(() => {
    if (!isEditing)
      reset(defaultValues);
  }, [reset, defaultValues]);

  useEffect(() => {
    if (defaultValues) {
      setValue('order', verificateNullValue(defaultValues?.order));
      setValue('name', verificateNullValue(defaultValues?.name));
      setValue('status', defaultValues?.status);
      setValue('billing_address', defaultValues?.billing_address);
      if (defaultValues?.status && !['paid', 'cancelled', 'expired'].includes(defaultValues?.status)) setHidden(false)
    }
  }, [defaultValues])

  return (
    <div className='mt-5'>
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className='mt-4'>
          {!isEditing && <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              control={control}
              name='order'
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <div className="orders">
                  <SearchOrder
                    showLabel={t('selectorder')}
                    onSelected={(item) => {
                      onChange(item)
                    }}
                    defaultOrder={value}
                    placeholder={t('searchByOrder')}
                  />
                </div>
              )}
            />
            {errors?.order && <small className="text-danger ">{errors.order.message}</small>}
          </Col>}
          {!isEditing && <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={t('name')}
                  placeholder={t('name')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.name && <small className="text-danger ">{errors.name.message}</small>}
          </Col>}
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="status"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={choices}
                  label={t('state')}
                  placeholder={t('select')}
                  value={value}
                  onCheckedItem={(e) => {
                    onChange(e)
                    setHidden(['paid', 'cancelled', 'expired'].includes(e))
                  }}
                />
              )}
            />
            {errors?.status && <small className="text-danger ">{errors.status.message}</small>}
          </Col>
          {!hidden && (
           <Col sm={12} md={12} lg={4} className="mb-4">
              <CustomCheckbox
                label={`${t('orderTrans.modify')} ${t('orderTrans.billingAddress')}`}
                checked={editBillingAddress}
                onChange={() => setEditBillingAddress(!editBillingAddress)}
              />
            </Col>
          )}
        </Row>
        {editBillingAddress && !hidden && <Row className='border border-secondary p-3 rounded mt-3'>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <CustomInput type="text" label={t('name')} {...field} placeholder={t('name')} />
              )}
            />
            {errors?.billing_address?.name && (
              <small className="text-danger">{errors.billing_address.name.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.last_name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <CustomInput
                  type="text"
                  label={t('last_name')}
                  {...field}
                  placeholder={t('last_name')}
                />
              )}
            />
            {errors?.billing_address?.last_name && (
              <small className="text-danger">{errors.billing_address.last_name.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: t('incorrectFormat'),
                },
              }}
              render={({ field }) => (
                <CustomInput
                  type="text"
                  label={t('orderTrans.email')}
                  {...field}
                  placeholder={t('orderTrans.email')}
                />
              )}
            />
            {errors?.billing_address?.email && (
              <small className="text-danger">{errors.billing_address.email.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.phone"
              control={control}
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: t('solonumeros'),
                },
                minLength: {
                  value: 6,
                  message: t('minimumLength'),
                },
                maxLength: {
                  value: 15,
                  message: t('maximumLength'),
                },
              }}
              render={({ field }) => (
                <CustomInput
                  type="text"
                  label={t('telefono')}
                  {...field}
                  placeholder={t('telefono')}
                />
              )}
            />
            {errors?.billing_address?.phone && (
              <small className="text-danger">{errors.billing_address.phone.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.address"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <CustomInput
                  type="text"
                  label={t('direccion')}
                  {...field}
                  placeholder={t('direccion')}
                />
              )}
            />
            {errors?.billing_address?.address && (
              <small className="text-danger">{errors.billing_address.address.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.postal_code"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <CustomInput
                  type="text"
                  label={t('postalcode')}
                  {...field}
                  placeholder={t('postalcode')}
                />
              )}
            />
            {errors?.billing_address?.postal_code && (
              <small className="text-danger">{errors.billing_address.postal_code.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.locality"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <CustomInput
                  type="text"
                  label={t('localidad')}
                  {...field}
                  placeholder={t('localidad')}
                />
              )}
            />
            {errors?.billing_address?.locality && (
              <small className="text-danger">{errors.billing_address.locality.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.province"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SearchProvince
                  filterBy="nombre"
                  placeholder={t('province')}
                  onSelected={(item) => onChange(item)}
                  defaultProvince={value}
                  invoice={true}
                />
              )}
            />
            {errors?.billing_address?.province && (
              <small className="text-danger">{errors.billing_address.province.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-4">
            <Controller
              name="billing_address.country"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SearchCountry
                  filterBy="nombre"
                  placeholder={t('pais')}
                  onSelected={(item) => onChange(item)}
                  defaultCountry={value}
                />
              )}
            />
            {errors?.billing_address?.country && (
              <small className="text-danger">{errors.billing_address.country.message}</small>
            )}
          </Col>
        </Row>}
      </Form>
      {defaultValues.articles && <div className='d-flex flex-column gap-1 mt-3'>
        <label>{t('exports.articles')}:</label>
        <Col md={12} className='d-flex flex-row p-2 border-grey bg-grey gap-3'>
          <ArticlesHeader />
        </Col>
        {defaultValues.articles.map((article) => (
          <Col md={12} className='d-flex flex-row p-2 gap-3'>
            <ArticlesHeader articles_texts={article} />
          </Col>
        ))}
      </div>}
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button
          color="primary"
          onClick={handleSubmit(onSubmitForm)}
          loading={loading}
        >
          {t('save')}
          <SpinnerComponent loading={loading} />
        </Button>
      </div>
    </div>
  );
};

InvoiceForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.any,
  loading: PropTypes.bool,
  isEditing: PropTypes.bool.isRequired,
};

export default InvoiceForm;